'use client';

import { useEffect, useRef, useState } from 'react';
import { useLocalTranslation } from '@/hooks/useLocalTranslation';
import { useAuthStore } from '@/stores/authStore';
import { getDeviceAccounts } from '@/utils/deviceAccounts';
import { getBotUrl } from '@/utils/telegram';
import { getBotUsername } from '@/utils/config';
import { getApiBaseUrl } from '@/utils/apiBaseUrl';
import { FaSteam } from '@/components/icons';
import translations from '@/screens/auth/LoginScreen/LoginScreen.translations';

interface TelegramLoginData {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

interface LoginMethodsProps {
  /**
   * Path to return to after login completes. Used by Steam OAuth which
   * routes through /login as its callback target — `shot_login_next`
   * survives the round-trip and `LoginScreen` reads it to redirect.
   * Defaults to current pathname so RequiresLogin walls land back on
   * the same screen.
   */
  next?: string;
}

/**
 * Shared inline auth surface: Telegram Login Widget + Steam OpenID
 * button. Drop into any screen instead of redirecting to /login.
 *
 * - Telegram widget's `data-auth-url` points to the current page so
 *   the OAuth callback returns to where the user started; the effect
 *   below consumes the signed query params, posts them to
 *   /api/auth/telegram-widget, and flips authStore. RequiresLogin
 *   re-renders into its children with no extra navigation.
 * - Steam OAuth routes through the backend → /login → AuthBootstrap;
 *   `next` is preserved via sessionStorage.
 */
export function LoginMethods({ next }: LoginMethodsProps) {
  const { t } = useLocalTranslation(translations);
  const markAuthed = useAuthStore((s) => s.markAuthed);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const widgetRef = useRef<HTMLDivElement>(null);

  const handleTelegramLogin = async (user: TelegramLoginData) => {
    setError(null);
    setLoading(true);
    try {
      const apiUrl = getApiBaseUrl();
      const response = await fetch(`${apiUrl}/api/auth/telegram-widget`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'shot',
        },
        body: JSON.stringify({ ...user, u: getDeviceAccounts() }),
        credentials: 'include',
      });

      if (!response.ok) {
        setError(t('loginError'));
        return;
      }

      const data = (await response.json()) as { userId: string };
      markAuthed(data.userId);
    } catch {
      setError(t('loginError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    const hash = params.get('hash');
    const authDate = params.get('auth_date');
    if (!id || !hash || !authDate) return;

    window.history.replaceState({}, '', window.location.pathname);

    handleTelegramLogin({
      id: Number(id),
      hash,
      auth_date: Number(authDate),
      first_name: params.get('first_name') || undefined,
      last_name: params.get('last_name') || undefined,
      username: params.get('username') || undefined,
      photo_url: params.get('photo_url') || undefined,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!widgetRef.current || isAuthenticated) return;

    // Redirect mode only. The popup/data-onauth path was unreliable in
    // practice: on mobile and several desktop browsers Telegram opened
    // its OAuth tab, the user confirmed, and the tab closed without
    // ever posting back to opener. Switching to data-auth-url makes
    // Telegram do a full-page redirect to the same path with the
    // signed query params, and the useEffect above picks them up.
    const target = window.location.origin + window.location.pathname + window.location.search;

    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.async = true;
    script.setAttribute('data-telegram-login', getBotUsername());
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-radius', '12');
    script.setAttribute('data-userpic', 'false');
    script.setAttribute('data-auth-url', target);
    script.setAttribute('data-request-access', 'write');
    widgetRef.current.appendChild(script);
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSteamLogin = () => {
    const target = next ?? window.location.pathname;
    if (target && target !== '/' && target !== '/login') {
      sessionStorage.setItem('shot_login_next', target);
    }
    window.location.href = `${getApiBaseUrl()}/api/users/login/steam`;
  };

  return (
    <div className="w-full max-w-[320px] flex flex-col gap-3">
      <div className="w-full flex items-center justify-center min-h-[46px]">
        {loading ? (
          <span className="text-sm font-medium text-white">{t('loggingIn')}</span>
        ) : (
          <div ref={widgetRef} />
        )}
      </div>

      <button
        type="button"
        onClick={handleSteamLogin}
        disabled={loading}
        className="w-full h-[46px] rounded-xl bg-[#171a21] hover:bg-[#2a475e] text-white font-semibold text-base flex items-center justify-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <FaSteam size={20} />
        <span>{t('loginWithSteam')}</span>
      </button>

      {error && <p className="text-red-400 text-sm text-center mt-2">{error}</p>}

      <button
        type="button"
        onClick={() => window.open(getBotUrl(), '_blank')}
        className="mt-2 text-sm text-white/50 hover:text-white/80 underline-offset-2 hover:underline transition-colors text-center"
      >
        {t('openInTelegram')}
      </button>
    </div>
  );
}
